/**
 * The wire. JSON text frames over one WebSocket per room.
 *
 * Identity (name + look) and the room travel in the connect URL, so the
 * Worker can validate them before the Durable Object ever wakes. After that
 * the client only sends intents; the room plans paths and fans events out.
 * Nothing ticks: a quiet room is a hibernated room.
 */
import type { Look } from './cosmetics.ts'
import type { Path } from './path.ts'
import type { RoomId } from './world.ts'

/** Bump whenever the server would disagree with an older client (the map counts). */
export const PROTOCOL_VERSION = 3
/** Largest frame either side accepts. */
export const MAX_FRAME = 1024
/** Client pings this often; the room answers without waking (auto-response). */
export const PING_MS = 25_000
/** The client lets go after this long without input, so idle tabs cost nothing and counts stay honest. */
export const IDLE_MS = 20 * 60_000

export const EMOTES = ['happy', 'love', 'surprised', 'sad', 'smug', 'wink', 'shy', 'scared', 'thinking', 'sick', 'sleepy', 'unsure'] as const
export type Emote = (typeof EMOTES)[number]
export const EMOTE_MS = 6000

export const ACTS = ['hop', 'spin', 'wiggle'] as const
export type Act = (typeof ACTS)[number]

/** Standing, sitting where you are, or napping. Seats make you sit on their own. */
export type Pose = 'stand' | 'sit' | 'nap'

export interface PlayerView {
  id: string
  name: string
  look: Look
  path: Path
  pose: Pose
  /** Last facing, ±1 (screen right / left). */
  face: 1 | -1
  emote?: { e: Emote; at: number }
}

export interface ChatLine { id: string; name: string; text: string; at: number }

export type ClientMsg =
  | { t: 'move'; x: number; y: number }
  | { t: 'say'; text: string }
  | { t: 'emote'; e: Emote }
  | { t: 'act'; a: Act }
  | { t: 'pose'; p: Pose }
  | { t: 'look'; look: Look }
  | { t: 'report'; id: string }
  /** Hop to a named place in this room (the map's "take me there"). */
  | { t: 'warp'; place: string }

export type ServerMsg =
  | { t: 'welcome'; v: number; you: string; room: RoomId; shard: number; now: number; players: PlayerView[]; chat: ChatLine[] }
  | { t: 'join'; p: PlayerView }
  | { t: 'leave'; id: string }
  | { t: 'path'; id: string; path: Path }
  | { t: 'say'; id: string; name: string; text: string; at: number }
  | { t: 'emote'; id: string; e: Emote; at: number }
  | { t: 'act'; id: string; a: Act; at: number }
  | { t: 'pose'; id: string; p: Pose; face: 1 | -1 }
  | { t: 'look'; id: string; look: Look }
  | { t: 'notice'; text: string }

/** Close codes the client acts on. */
export const CLOSE = {
  /** The same player opened this room in another tab. */
  replaced: 4001,
  full: 4002,
  /** Too many bad or excessive frames. */
  abuse: 4003,
  /** Server is restarting / room moved; reconnect. */
  restart: 4004,
} as const

/** Parse an untrusted client frame into a well-formed message, or null. */
export function parseClient(raw: unknown): ClientMsg | null {
  if (typeof raw !== 'string' || raw.length > MAX_FRAME) return null
  let o: Record<string, unknown>
  try {
    const v = JSON.parse(raw)
    if (!v || typeof v !== 'object' || Array.isArray(v)) return null
    o = v as Record<string, unknown>
  } catch {
    return null
  }
  switch (o.t) {
    case 'move':
      return Number.isInteger(o.x) && Number.isInteger(o.y) ? { t: 'move', x: o.x as number, y: o.y as number } : null
    case 'say':
      return typeof o.text === 'string' ? { t: 'say', text: o.text } : null
    case 'emote':
      return (EMOTES as readonly string[]).includes(o.e as string) ? { t: 'emote', e: o.e as Emote } : null
    case 'act':
      return (ACTS as readonly string[]).includes(o.a as string) ? { t: 'act', a: o.a as Act } : null
    case 'pose':
      return o.p === 'stand' || o.p === 'sit' || o.p === 'nap' ? { t: 'pose', p: o.p } : null
    case 'look':
      return o.look && typeof o.look === 'object' ? { t: 'look', look: o.look as Look } : null
    case 'report':
      return typeof o.id === 'string' && o.id.length <= 16 ? { t: 'report', id: o.id } : null
    case 'warp':
      return typeof o.place === 'string' && o.place.length <= 24 ? { t: 'warp', place: o.place } : null
    default:
      return null
  }
}

/** Token bucket. Plain data so it can live in a hibernation attachment if ever needed. */
export interface Bucket { tokens: number; at: number }

export function take(b: Bucket, now: number, rate: number, burst: number, cost = 1): boolean {
  b.tokens = Math.min(burst, b.tokens + ((now - b.at) / 1000) * rate)
  b.at = now
  if (b.tokens < cost) return false
  b.tokens -= cost
  return true
}

/** Per-intent limits: [refill per second, burst]. */
export const LIMITS: Record<ClientMsg['t'], [number, number]> = {
  move: [8, 16],
  say: [0.5, 4],
  emote: [2, 5],
  act: [2, 5],
  pose: [2, 5],
  look: [0.5, 3],
  report: [0.1, 3],
  warp: [0.2, 2],
}
